'use client'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  Column,
  Table as ReactTable,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import React, { CSSProperties } from 'react'
import { cn } from '@/lib/utils'

import { useState, useRef, useEffect } from 'react'
import autoAnimate from '@formkit/auto-animate'

// needed for table body level scope DnD setup
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  PointerSensor,
  pointerWithin,
  DragOverlay,
  closestCorners,
  rectIntersection,
} from '@dnd-kit/core'
import {
  restrictToVerticalAxis,
  restrictToParentElement,
  restrictToHorizontalAxis,
} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

// needed for row & cell level scope DnD setup
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { GripVerticalIcon } from 'lucide-react'
import { ENTITY_TYPES } from '@/data/constants/constants'
import usePlaceVaultInGroup from '@/services/api/vault/usePlaceVaultInGroup'
import usePlaceTransactionCategoryInGroup from '@/services/api/transaction-category/usePlaceTransactionCategoryInGroup'
import useRemoveTransactionCategoryFromGroup from '@/services/api/transaction-category/useRemoveTransactionCategoryFromGroup'
import useRemoveVaultFromGroup from '@/services/api/vault/useRemoveVaultFromGroup'
import useSortVault from '@/services/api/vault/useSortVault'
import useSortTransactionCategory from '@/services/api/transaction-category/useSortTransactionCategory'

const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned()
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left')
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right')

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-4px 0 4px -4px gray inset'
      : isFirstRightPinnedColumn
        ? '4px 0 4px -4px gray inset'
        : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    // opacity: isPinned ? 0.95 : 1,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    // backgroundColor: isPinned ? 'white' : 'initial',
    ...(isPinned ? { '--tw-bg-opacity': 1 } : {}),
  }
}

interface DataTableProps<TData> {
  table: ReactTable<TData>
  className?: string
}

const acceptedTypesLookup = {
  [ENTITY_TYPES.VAULT]: [ENTITY_TYPES.VAULT, ENTITY_TYPES.VAULT_GROUP],
  [ENTITY_TYPES.VAULT_GROUP]: [ENTITY_TYPES.VAULT, ENTITY_TYPES.VAULT_GROUP],
  [ENTITY_TYPES.TRANSACTION_CATEGORY]: [
    ENTITY_TYPES.TRANSACTION_CATEGORY,
    ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP,
  ],
  [ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP]: [
    ENTITY_TYPES.TRANSACTION_CATEGORY,
    ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP,
  ],
}

function sameRow(active, over) {
  return over?.data?.current?.id === active?.data?.current?.id
}

function interactionBetweenMoneyInAndMoneyOut(active, over) {
  const overRow = over?.data?.current
  const activeRow = active?.data?.current

  return (
    [
      ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP,
      ENTITY_TYPES.TRANSACTION_CATEGORY,
    ].includes(activeRow?.original?.entity_type) &&
    activeRow?.original?.direction !== overRow?.original?.direction
  )
}

const canDrop = (active, over) => {
  const overRow = over?.data?.current
  const activeRow = active?.data?.current

  const acceptedTypes = acceptedTypesLookup[overRow?.original?.entity_type]

  // If the active item is not in the accepted types, do not allow drop
  if (!acceptedTypes?.includes(activeRow?.original?.entity_type)) {
    return false
  }

  // Do not allow to drop in if the row is not a group
  if (!overRow?.original?.is_group) {

    return false
  }

  // If draging over the same item
  if (sameRow(active, over)) {
    return false
  }

  // Both should not be groups - Do not allow a group placing into another group
  if (overRow?.original?.is_group && activeRow?.original?.is_group) {
    return false
  }

  // Do not allow directly dropping vault into archived group
  if (
    overRow?.original?.entity_type === ENTITY_TYPES.VAULT_GROUP &&
    overRow?.original?.is_archive
  ) {
    return false
  }

  // Do not allow dropping archived vault into non-archived groups
  if (
    activeRow?.original?.entity_type === ENTITY_TYPES.VAULT &&
    activeRow?.original?.is_archived
  ) {
    return false
  }

  if (interactionBetweenMoneyInAndMoneyOut(active, over)) {
    return false
  }

  // Avoid dropping into the same Group
  if (activeRow?.parentId === overRow?.id) {
    return false
  }

  return true
}

const canDropTopOrBottom = (active, over, area) => {
  const overRow = over?.data?.current
  const activeRow = active?.data?.current

  // If draging over the same item
  if (sameRow(active, over)) {
    return false
  }

  const acceptedTypes = acceptedTypesLookup[overRow?.original?.entity_type]

  // If the active item is not in the accepted types, do not allow drop
  if (!acceptedTypes?.includes(activeRow?.original?.entity_type)) {
    return false
  }

  // Restrict dropping into archived groups
  if (
    (!activeRow?.original?.is_archived && overRow?.original?.is_archived) ||
    (overRow?.original?.is_group && overRow?.original?.is_archive)
  ) {

    return false
  }

  // Disable sorting if the active item is over the bottom of the top row or the top of the bottom row
  const sortableIds = activeRow?.sortable?.items
  if (sortableIds) {
    const activeItemIndex = sortableIds.indexOf(activeRow?.id)
    const overItemIndex = sortableIds.indexOf(overRow?.id)

    if (
      (activeItemIndex - 1 === overItemIndex && area !== 'top') ||
      (activeItemIndex + 1 === overItemIndex && area !== 'bottom')
    ) {
      return false
    }
  }

  // Do not allow sorting between vault group and vault inside a group
  if (
    activeRow?.original?.entity_type === ENTITY_TYPES.VAULT_GROUP &&
    activeRow?.depth === 0 &&
    overRow?.original.entity_type === ENTITY_TYPES.VAULT &&
    overRow?.depth > 0
  ) {
    return false
  }

  // Do not allow dropping archived vault into non-archived groups
  if (
    activeRow?.original?.entity_type === ENTITY_TYPES.VAULT &&
    activeRow?.original?.is_archived &&
    !overRow?.original?.is_archived
  ) {
    return false
  }

  if (interactionBetweenMoneyInAndMoneyOut(active, over)) {
    return false
  }

  // Trying to move a group item into its own parent
  if (activeRow?.parentId === overRow?.id) {
    return false
  }

  return true
}

export function DataTable<TData>({ table, className }: DataTableProps<TData>) {
  const parent = useRef(null)

  useEffect(() => {
    parent.current && autoAnimate(parent.current)
  }, [parent])

  const { action: placeVaultInGroupAction, status: placeVaultInGroupStatus } =
    usePlaceVaultInGroup()

  const { action: placeCategoryInGroup, status: placeCategoryInGroupStatus } =
    usePlaceTransactionCategoryInGroup()

  const {
    action: removeTransactionCategoryFromGroup,
    status: removeTransactionCategoryFromGroupStatus,
  } = useRemoveTransactionCategoryFromGroup()

  const { action: removeVaultFromGroup, status: removeVaultFromGroupStatus } =
    useRemoveVaultFromGroup()

  const { action: sortVault, status: sortVaultStatus } = useSortVault()
  const { action: sortCategory, status: sortCategoryStatus } =
    useSortTransactionCategory()

  // reorder rows after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over, activatorEvent, delta } = event
    const activeRow = active?.data?.current
    const overRow = over?.data?.current

    function getTargerSortOrder() {
      const sortDirection = delta.x > 0 ? 'down' : 'up'

      let inc = 0

      if (sortDirection === 'down') {
        if (over?.id?.includes('mydrop-top')) {
          inc = -1
        } else if (over?.id?.includes('mydrop-bottom')) {
          inc = 1
        }
      } else if (sortDirection === 'up') {
        if (over?.id?.includes('mydrop-top')) {
          inc = -1
        } else if (over?.id?.includes('mydrop-bottom')) {
          inc = 1
        }
      }

      return overRow?.original?.sort_order + inc
    }

    if (!over?.id?.includes('mydrop')) {
      return
    }

    const directlyDroppedInGroup = over?.id?.includes('middle')
    let changeGroup = false
    let targetGroupId = null

    if (directlyDroppedInGroup) {
      changeGroup = true
      targetGroupId = overRow?.original?.id
    }

    if (
      !directlyDroppedInGroup &&
      // overRow?.parentId &&
      activeRow?.parentId !== overRow?.parentId &&
      activeRow?.parentId !== overRow?.id
    ) {
      changeGroup = true
      targetGroupId = overRow?.getParentRow()?.original?.id
    }

    if (changeGroup) {
      if (activeRow.original.entity_type === ENTITY_TYPES.VAULT) {
        if (!targetGroupId) {
          return removeVaultFromGroup({
            id: activeRow?.original?.id,
            sortOrder: getTargerSortOrder(),
          })
        }

        return placeVaultInGroupAction({
          vaultId: activeRow?.original?.id,
          groupId: targetGroupId,
          sortOrder: getTargerSortOrder(),
        })
      }

      // Change transaction category group
      if (
        activeRow.original.entity_type === ENTITY_TYPES.TRANSACTION_CATEGORY
      ) {
        if (!targetGroupId) {
          // Remove from group
          return removeTransactionCategoryFromGroup({
            id: activeRow?.original?.id,
            sortOrder: getTargerSortOrder(),
          })
        }

        return placeCategoryInGroup({
          categoryId: activeRow?.original?.id,
          groupId: targetGroupId,
          sortOrder: getTargerSortOrder(),
        })
      }
      return
    }

    // Handle sort
    if (
      !(
        over?.id?.includes('mydrop-top') ||
        over?.id?.includes('mydrop-bottom') ||
        over?.id?.includes('mydrop-middle')
      )
    ) {
      return
    }

    const sortTo = getTargerSortOrder()

    if (
      [ENTITY_TYPES.VAULT, ENTITY_TYPES.VAULT_GROUP].includes(
        activeRow.original.entity_type,
      )
    ) {
      return sortVault({
        id: activeRow?.original?.id,
        entityType:
          activeRow?.original?.entity_type === ENTITY_TYPES.VAULT
            ? 'vaults'
            : 'vault_groups',
        sortOrder: sortTo,
      })
    }

    if (
      [
        ENTITY_TYPES.TRANSACTION_CATEGORY,
        ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP,
      ].includes(activeRow.original.entity_type)
    ) {
      return sortCategory({
        id: activeRow?.original?.id,
        entityType:
          activeRow?.original?.entity_type === ENTITY_TYPES.TRANSACTION_CATEGORY
            ? 'transaction_categories'
            : 'transaction_category_groups',
        sortOrder: sortTo,
      })
    }

    // if (active && over && active.id !== over.id) {
    //   setData((data) => {
    //     const oldIndex = dataIds.indexOf(active.id)
    //     const newIndex = dataIds.indexOf(over.id)
    //     return arrayMove(data, oldIndex, newIndex) //this is just a splice util
    //   })
    // }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const dataIds = React.useMemo<UniqueIdentifier[]>(() => {
    return table.getRowModel().rows.map((row) => {
      return row.id
    })
  }, [table.getRowModel().rows])

  const busy = [
    placeCategoryInGroupStatus.status,
    placeVaultInGroupStatus.status,
    removeTransactionCategoryFromGroupStatus.status,
    removeVaultFromGroupStatus.status,
    sortVaultStatus.status,
    sortCategoryStatus.status,
  ].includes('pending')

  return (
    <div
      data-busy={busy ? true : undefined}
      className={cn(
        'rounded-md border transition-all data-[busy]:pointer-events-none data-[busy]:animate-pulse data-[busy]:opacity-50',
        className,
      )}
    >
      <DndContext
        collisionDetection={pointerWithin}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <Table className="kiruab-table overflow-auto">
          <TableHeader className="sticky top-0 z-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="h-[60px]">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{ ...getCommonPinningStyles(header.column) }}
                      className="w-[156px] bg-white"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody
          // ref={parent}
          >
            <SortableContext
              items={dataIds}
              strategy={verticalListSortingStrategy}
            >
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  return <RowComponent key={row.id} row={row} />
                })
              ) : (
                <TableRow>
                  <TableCell
                    // colSpan={table.getAllColumns().length}
                    className=" text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
    </div>
  )
}

function RowComponent({ row }) {
  const {
    transform,
    transition,
    setNodeRef,
    isDragging,
    isOver,
    isSorting,
    over,
    active,
    setDroppableNodeRef,
    data,
  } = useSortable({
    id: row.id,
    disabled: true,
    data: row,
  })

  const dragAreas = {
    bottom: 'bottom',
    middle: 'middle',
    top: 'top',
  }

  const style: CSSProperties = {
    // transform: isDragging ? CSS.Transform.toString(transform) : 'none',
    // transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 10 : 0,
    position: 'relative',
  }

  const droppableInMiddle = canDrop(active, over)
  // const droppableInTopOrBottom = canDropTopOrBottom(active, over)
  const { setNodeRef: setNodeRefTop, isOver: isDropOverTop } = useDroppable({
    id: `mydrop-top-${row.id}`,
    data: row,
    disabled: !canDropTopOrBottom(active, over, 'top'),
  })

  const { setNodeRef: setNodeRefBottom, isOver: isDropOverBottom } =
    useDroppable({
      id: `mydrop-bottom-${row.id}`,
      data: row,
      disabled: !canDropTopOrBottom(active, over, 'bottom'),
    })

  const { setNodeRef: setNodeRefMiddle, isOver: isDropOverMiddle } =
    useDroppable({
      id: `mydrop-middle-${row.id}`,
      data: row,
      disabled: !droppableInMiddle,
    })

  const dragArea = isDropOverMiddle
    ? dragAreas.middle
    : isDropOverTop
      ? dragAreas.top
      : isDropOverBottom
        ? dragAreas.bottom
        : undefined

  const getCellColorClass = (entityType, direction) => {
    switch (entityType) {
      case ENTITY_TYPES.VAULT_GROUP:
        return 'bg-[#E4F1FF]'
      case ENTITY_TYPES.MONEY_IN_HEADER:
        return 'bg-green-lowest font-medium'
      case ENTITY_TYPES.MONEY_OUT_HEADER:
        return 'bg-purple-lowest font-medium'
      case ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP:
        // return direction === -1 ? 'bg-[#F7F2FE]' : 'bg-[#E5F4E9]'
        return direction === -1
          ? 'bg-[#F5EEFA] font-medium'
          : 'bg-[#E5F4E9] font-medium'
      case ENTITY_TYPES.TRANSACTION_CATEGORY:
        return direction === -1 ? 'bg-[#FAF5FE]' : 'bg-[#EEF7F0]'
      default:
        return ''
    }
  }

  const cellColorClass = getCellColorClass(
    row.original?.entity_type,
    row.original?.direction,
  )

  return (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      data-drag-area={dragArea}
      className="group/row relative h-[52px] !opacity-100"
      style={isDragging ? style : {}}
      ref={setNodeRef}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          data-money-in-header={
            row.original?.entity_type === ENTITY_TYPES.MONEY_IN_HEADER
              ? true
              : undefined
          }
          data-money-out-header={
            row.original?.entity_type === ENTITY_TYPES.MONEY_OUT_HEADER
              ? true
              : undefined
          }
          data-isgroup={row.original?.is_group ? true : undefined}
          key={cell.id}
          style={{ ...getCommonPinningStyles(cell.column) }}
          className={`group/cell data-[isgroup]:!bg-[#E4F1FF]--- data-[money-in-header]:bg-green-lowest--- data-[money-out-header]:bg-purple-lowest--- relative bg-white transition-all group-hover/row:bg-opacity-15 [&:has([data-negative])]:bg-yellow-lowest ${isDragging ? 'z-40-- opacity-50' : ''} ${cellColorClass}`}
          data-first-col={cell.column.getIsFirstColumn() ? true : undefined}
        >
          <div
            data-first-col={cell.column.getIsFirstColumn() ? true : undefined}
            className="-ml-4 grid pl-1 data-[first-col]:grid-cols-[1.75rem,1fr]"
          >
            {cell.column.getIsFirstColumn() ? (
              <div>
                {isSorting && (
                  <div className="pointer-events-none absolute bottom-0 left-0 top-0 grid h-full w-full grid-rows-[37.5%_1fr_37.5%] opacity-0">
                    <div
                      ref={setNodeRefTop}
                      className="drag-are-top bg-green-400"
                    ></div>

                    <div
                      ref={setNodeRefMiddle}
                      className="drag-are-middle bg-red-400"
                    ></div>

                    <div
                      ref={setNodeRefBottom}
                      className="drag-are-bottom bg-gray-400 "
                    ></div>
                  </div>
                )}

                <DragOverlay className="drag-overlya-k cursor-pointer outline-dotted outline-gray-300"></DragOverlay>

                <div className="flex items-center opacity-0 group-hover/cell:opacity-100">
                  <RowDragHandleCell
                    rowId={row.id}
                    rowItem={row.original}
                    row={row}
                  ></RowDragHandleCell>
                </div>
              </div>
            ) : null}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        </TableCell>
      ))}
      <td className="pointer-events-none m-0 w-0 p-0">
        {/* <TooltipProvider>
          <Tooltip open={!!dragArea}>
            <TooltipTrigger asChild>
              <div
                data-drag-area={dragArea}
                className={`high pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-50 h-[calc(100%+0px)] w-full border-transparent opacity-0 transition-all data-[drag-area=middle]:border-2 data-[drag-area=bottom]:border-b-2 data-[drag-area=top]:border-t-2 data-[drag-area]:border-accent data-[drag-area]:opacity-100`}
              ></div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent align="start" side="top">
                Edit transfer
                <TooltipArrow />
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider> */}
        {/* Highlight with border */}
        <div
          data-drag-area={dragArea}
          className={`high pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-50 h-[calc(100%+0px)] w-full border-transparent opacity-0 transition-all data-[drag-area=middle]:border-2 data-[drag-area=bottom]:border-b-2 data-[drag-area=top]:border-t-2 data-[drag-area]:border-accent data-[drag-area]:opacity-100`}
        ></div>
      </td>
    </TableRow>
  )
}

const RowDragHandleCell = ({ rowId, rowItem, row }) => {
  const disabled = ['money_in_header', 'money_out_header'].includes(
    row.original.entity_type,
  )
  const { attributes, listeners } = useSortable({
    id: rowId,
    data: row,
    disabled,
  })

  const onMouseDown = (e) => {
    row.toggleExpanded(false)
    listeners.onMouseDown(e)
  }

  const onKeyDown = (e) => {
    row.toggleExpanded(false)
    listeners.onKeyDown(e)
  }

  const onTouchStart = (e) => {
    row.toggleExpanded(false)
    listeners.onTouchStart(e)
  }

  return (
    <Button
      iconOnly
      size="sm"
      className={`size-7 ${disabled ? 'hidden' : ''}`}
      variant="ghost"
      {...attributes}
      {...listeners}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
    >
      <GripVerticalIcon className="" />
    </Button>
    // </div>
  )
}
